'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Ably from 'ably';
import { useChannel } from 'ably/react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

import { apiRoutes, getApiUrl } from '@/constants/api';
import { clientApiFetcher } from '@/lib/api/fetcher/client';
import {
  deleteNotification,
  getNotificationUnreadCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '@/lib/api/notification';
import type { Notification } from '@/types/notifications';

type NotificationPageResponse = {
  notifications: Notification[];
  nextCursor: string | null;
  hasMore: boolean;
};

const MAX_NOTIFICATIONS_TO_SHOW = 10; // Always show exactly 10 notifications, newest first

/**
 * An infinite scroll hook that fetches notifications with pagination.
 * Uses a dedicated endpoint for unread counts and handles real-time updates properly.
 */
export function useInfiniteNotifications(userId?: string, limit: number = 10) {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [localDeletions, setLocalDeletions] = useState<Set<string>>(new Set());
  const [localMarkAsRead, setLocalMarkAsRead] = useState<Set<string>>(
    new Set(),
  );
  const [newNotifications, setNewNotifications] = useState<Notification[]>([]);
  const [scrollContainerReady, setScrollContainerReady] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Reset local state when user changes
  useEffect(() => {
    setLocalDeletions(new Set());
    setLocalMarkAsRead(new Set());
    setNewNotifications([]);
    setScrollContainerReady(false);
  }, [userId]);

  // Fetch unread count separately
  const { data: unreadCountData, mutate: mutateUnreadCount } = useSWR<{
    unreadCounts: {
      all: number;
      messages: number;
      general: number;
    };
  }>(
    userId ? getApiUrl(apiRoutes.notification.getUnreadCount) : null,
    () => getNotificationUnreadCount(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      refreshInterval: 0,
    },
  );

  // The key that SWR will use to fetch the data for each page
  const getKey = (
    pageIndex: number,
    previousPageData: NotificationPageResponse | null,
  ) => {
    if (!userId) return null;

    if (previousPageData && !previousPageData.hasMore) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('limit', limit.toString());

    if (pageIndex === 0) {
      return getApiUrl(apiRoutes.notification.getAll(params.toString()));
    }

    if (previousPageData?.nextCursor) {
      params.set('cursor', previousPageData.nextCursor);
    }

    return getApiUrl(apiRoutes.notification.getAll(params.toString()));
  };

  const { data, error, size, setSize, isLoading, isValidating, mutate } =
    useSWRInfinite<NotificationPageResponse>(getKey, clientApiFetcher, {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateFirstPage: false,
      persistSize: true,
      revalidateIfStale: false,
      dedupingInterval: 8000,
      focusThrottleInterval: 15000,
      errorRetryCount: 3,
      errorRetryInterval: 1000,
      onError: (error) => {
        console.error('Notifications fetch error:', error);
      },
    });

  const hasMore = data ? data[data.length - 1]?.hasMore : false;

  // Selective cache invalidation for new notifications
  const invalidateForNewNotification = useCallback(
    (newNotification: Notification) => {
      // For notifications, we can directly add to cache without refetching
      // since notifications are append-only and don't require complex ordering
      mutate(
        (currentData) => {
          if (!currentData || currentData.length === 0) return currentData;

          // Add the new notification to the first page
          const firstPage = currentData[0];
          const notificationExists = firstPage.notifications.some(
            (notif) => notif._id === newNotification._id,
          );

          if (notificationExists) return currentData;

          // Add new notification to the beginning (newest first)
          const updatedNotifications = [
            newNotification,
            ...firstPage.notifications,
          ];

          const updatedFirstPage = {
            ...firstPage,
            notifications: updatedNotifications,
          };

          return [updatedFirstPage, ...currentData.slice(1)];
        },
        {
          revalidate: false, // Don't refetch since we're updating cache directly
        },
      );
    },
    [mutate],
  );

  // Method to manually refresh notifications (useful for edge cases)
  const refreshNotifications = useCallback(() => {
    mutate(
      undefined, // Don't update cache data
      {
        revalidate: (pageData, pageKey) => {
          // For notifications, we want to revalidate only the first page
          return (
            pageKey ===
            getApiUrl(apiRoutes.notification.getAll(`limit=${limit}`))
          );
        },
      },
    );
  }, [mutate, limit]);

  // Ably real-time update handlers
  const handleNotificationUpdate = useCallback(
    (message: Ably.Message) => {
      if (message.name !== 'notification-update') return;

      const notification = message.data as Notification;

      // Add new notification to local state for immediate display
      setNewNotifications((prev) => {
        // Check if notification already exists
        const exists = prev.some((n) => n._id === notification._id);
        if (exists) return prev;

        // Add to top and pop off last if we're at capacity
        const updated = [notification, ...prev];
        return updated.length > MAX_NOTIFICATIONS_TO_SHOW
          ? updated.slice(0, MAX_NOTIFICATIONS_TO_SHOW)
          : updated;
      });

      // Update cache directly to ensure consistency on refresh
      invalidateForNewNotification(notification);

      mutateUnreadCount();
    },
    [mutateUnreadCount, invalidateForNewNotification],
  );

  const handleNotificationDelete = useCallback(
    (message: Ably.Message) => {
      if (message.name !== 'notification-delete') return;

      const { notificationId } = message.data as { notificationId: string };

      // Find if the deleted notification was unread
      const deletedNotification = [
        ...newNotifications,
        ...(data?.flatMap((page) => page.notifications) || []),
      ].find((n) => n._id === notificationId);

      setLocalDeletions((prev) => new Set(prev).add(notificationId));
      setNewNotifications((prev) =>
        prev.filter((n) => n._id !== notificationId),
      );

      // Refetch unread count if deleted notification was unread
      if (deletedNotification && !deletedNotification.read) {
        mutateUnreadCount();
      }
    },
    [newNotifications, data, mutateUnreadCount],
  );

  const handleNotificationMarkRead = useCallback(
    (message: Ably.Message) => {
      if (message.name !== 'notification-mark-read') return;

      const { notificationId } = message.data as { notificationId: string };

      // Check if the notification was unread before marking as read
      const targetNotification = [
        ...newNotifications,
        ...(data?.flatMap((page) => page.notifications) || []),
      ].find((n) => n._id === notificationId);

      setLocalMarkAsRead((prev) => new Set(prev).add(notificationId));

      // Update new notifications list
      setNewNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n)),
      );

      // Refetch unread count if notification was unread
      if (targetNotification && !targetNotification.read) {
        mutateUnreadCount();
      }
    },
    [newNotifications, data, mutateUnreadCount],
  );

  const handleNotificationMarkAllRead = useCallback(
    (message: Ably.Message) => {
      if (message.name !== 'notification-mark-all-read') return;

      // Mark all notifications as read locally
      setNewNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

      // Refetch unread count from server
      mutateUnreadCount();
    },
    [mutateUnreadCount],
  );

  // Subscribe to real-time updates
  useChannel(userId ? `user-${userId}-notifications` : '', (message) => {
    switch (message.name) {
      case 'notification-update':
        handleNotificationUpdate(message);
        break;
      case 'notification-delete':
        handleNotificationDelete(message);
        break;
      case 'notification-mark-read':
        handleNotificationMarkRead(message);
        break;
      case 'notification-mark-all-read':
        handleNotificationMarkAllRead(message);
        break;
    }
  });

  // Get all paginated notifications for infinite scroll
  const allPaginatedNotifications = useMemo(() => {
    if (!data?.length) return [];

    const allFromPages: Notification[] = [];
    data.forEach((page) => {
      page.notifications.forEach((notification) => {
        if (!localDeletions.has(notification._id)) {
          allFromPages.push({
            ...notification,
            read: localMarkAsRead.has(notification._id) || notification.read,
          });
        }
      });
    });
    return allFromPages;
  }, [data, localDeletions, localMarkAsRead]);

  // Combine all notifications and show exactly 10 for popup, more for infinite scroll
  const allNotifications = useMemo(() => {
    // Start with paginated notifications (these come from the server)
    const baseNotifications = allPaginatedNotifications;

    // If no new notifications, just show the paginated ones
    if (newNotifications.length === 0) {
      return baseNotifications;
    }

    // Create a unified list: new notifications + paginated notifications
    const existingIds = new Set(baseNotifications.map((n) => n._id));
    const uniqueNewNotifications = newNotifications.filter(
      (n) => !existingIds.has(n._id) && !localDeletions.has(n._id),
    );

    // Combine and sort by creation date (newest first)
    const allCombined = [...uniqueNewNotifications, ...baseNotifications].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    // For popup: show only the 10 most recent
    // For infinite scroll: show all loaded notifications
    const hasLoadedMoreThanOnePage = data && data.length > 1;

    return hasLoadedMoreThanOnePage
      ? allCombined // Show all when user has scrolled for more
      : allCombined.slice(0, MAX_NOTIFICATIONS_TO_SHOW); // Show only 10 initially
  }, [data, newNotifications, localDeletions, allPaginatedNotifications]);

  // Get notification counts
  const getCounts = useCallback(() => {
    if (!unreadCountData?.unreadCounts) {
      return {
        all: 0,
        messages: 0,
        general: 0,
      };
    }

    return {
      all: unreadCountData.unreadCounts.all,
      messages: unreadCountData.unreadCounts.messages,
      general: unreadCountData.unreadCounts.general,
    };
  }, [unreadCountData]);

  // Mark notification as read
  const markAsRead = useCallback(
    async (id: string) => {
      // Optimistic update
      setLocalMarkAsRead((prev) => new Set(prev).add(id));
      setNewNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n)),
      );

      // Optimistically update unread count
      if (
        unreadCountData?.unreadCounts &&
        unreadCountData.unreadCounts.all > 0
      ) {
        mutateUnreadCount(
          {
            unreadCounts: {
              ...unreadCountData.unreadCounts,
              all: unreadCountData.unreadCounts.all - 1,
            },
          },
          false,
        );
      }

      try {
        await markNotificationAsRead(id);
        // Refresh count from server to ensure accuracy
        await mutateUnreadCount();
      } catch (err) {
        console.error('Failed to mark notification as read', err);
        // Revert optimistic updates
        setLocalMarkAsRead((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        setNewNotifications((prev) =>
          prev.map((n) => (n._id === id ? { ...n, read: false } : n)),
        );
        await mutateUnreadCount();
      }
    },
    [unreadCountData, mutateUnreadCount],
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    setNewNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    mutateUnreadCount(
      {
        unreadCounts: {
          all: 0,
          messages: 0,
          general: 0,
        },
      },
      false,
    );

    try {
      await markAllNotificationsAsRead();
      await mutate();
      await mutateUnreadCount();
    } catch (err) {
      console.error('Failed to mark all notifications as read', err);
      // Refresh from server on error
      await mutate();
      await mutateUnreadCount();
    }
  }, [mutate, mutateUnreadCount]);

  // Remove notification
  const removeNotification = useCallback(
    async (id: string) => {
      const notificationToDelete = allNotifications.find((n) => n._id === id);

      // Optimistic update
      setLocalDeletions((prev) => new Set(prev).add(id));
      setNewNotifications((prev) => prev.filter((n) => n._id !== id));

      // If deleting an unread notification, update count
      if (
        notificationToDelete &&
        !notificationToDelete.read &&
        unreadCountData
      ) {
        mutateUnreadCount(
          {
            unreadCounts: {
              ...unreadCountData.unreadCounts,
              all: Math.max(0, unreadCountData.unreadCounts.all - 1),
            },
          },
          false,
        );
      }

      try {
        await deleteNotification(id);
        await mutateUnreadCount();
      } catch (err) {
        console.error('Failed to delete notification', err);
        // Revert optimistic updates
        setLocalDeletions((prev) => {
          const newDeletions = new Set(prev);
          newDeletions.delete(id);
          return newDeletions;
        });
        await mutateUnreadCount();
      }
    },
    [allNotifications, unreadCountData, mutateUnreadCount],
  );

  // Callback ref to handle scroll container setup
  const setScrollContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      scrollContainerRef.current = element;
      setScrollContainerReady(!!element);
    },
    [],
  );

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || isLoading || !hasMore || isLoadingMore) {
      return;
    }

    // Check if the container is actually scrollable (has content)
    if (scrollContainerRef.current) {
      if (
        scrollContainerRef.current.scrollHeight <=
        scrollContainerRef.current.clientHeight
      ) {
        return;
      }
    }

    // Clean up any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isValidating &&
          !isLoadingMore
        ) {
          setIsLoadingMore(true);
          setSize(size + 1);
        }
      },
      {
        threshold: 0.1,
        root: scrollContainerRef.current || null,
        rootMargin: '0px 0px 150px 0px', // Trigger 150px before reaching the bottom
      },
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    hasMore,
    isLoading,
    isValidating,
    isLoadingMore,
    setSize,
    size,
    allNotifications.length, // Watch for content changes
    scrollContainerReady, // Watch for when container becomes ready
  ]);

  // Reset loading state when validation completes
  useEffect(() => {
    if (!isValidating && isLoadingMore) {
      setIsLoadingMore(false);
    }
  }, [isValidating, isLoadingMore]);

  return {
    // Notification data
    notifications: allNotifications,
    getCounts,

    // Loading states
    isLoading: isLoading && allNotifications.length === 0,
    isLoadingMore,
    error,
    hasMore,

    // Actions
    markAsRead,
    markAllAsRead,
    removeNotification,
    mutate,

    // Cache management
    invalidateForNewNotification,
    refreshNotifications,

    // Infinite scroll
    loadMoreRef,
    setScrollContainerRef,
  };
}
